<script lang="ts">
  import CodeBlock from '../CodeBlock.svelte';
  import { formatCount } from '../../lib/complexity/format';
  import { OPS_PER_SEC, removeFirstShifts, simulateWindow, WINDOW_EVENTS } from '../../lib/labs';

  /**
   * ラボ 2 の手順 0（labs.md）: 窓口の操作列を配列 1 本と removeFirst() で処理し、
   * 答えは合うが、人数を増やすと詰め直しの回数が膨れることを数字で見せる。
   */
  interface Props {
    ondone: () => void;
  }

  let { ondone }: Props = $props();

  const sim = simulateWindow(WINDOW_EVENTS);
  let pos = $state(0);
  let n: number | null = $state(null);

  const shown = $derived(pos === 0 ? null : (sim.steps[pos - 1] ?? null));
  const line = $derived(shown?.line ?? []);
  const called = $derived(
    sim.steps.slice(0, pos).flatMap((s) => (s.called === undefined ? [] : [s.called])),
  );
  const shiftedSoFar = $derived(sim.steps.slice(0, pos).reduce((a, s) => a + s.shifted, 0));
  const done = $derived(pos >= sim.steps.length);

  const CODE = `var line: [Int] = []
line.append(1)                // 来る 1
line.append(2)                // 来る 2
print(line.removeFirst())     // 呼ぶ
line.append(3)                // 来る 3
print(line.removeFirst())     // 呼ぶ
print(line.removeFirst())     // 呼ぶ`;

  const SIZES = [6, 100, 200_000] as const;
  const shifts = $derived(n === null ? 0 : removeFirstShifts(n));
  const seconds = $derived(shifts / OPS_PER_SEC);

  function label(e: (typeof WINDOW_EVENTS)[number]): string {
    return e.type === 'arrive' ? `来る ${e.id}` : '呼ぶ';
  }
</script>

<section class="panel intro" aria-labelledby="intro-title" data-testid="lab-intro">
  <h2 id="intro-title">まず、配列 1 本と removeFirst() で処理してみる</h2>
  <p>
    窓口に人が来て、来た順に呼びます。来た人を配列の末尾に足し、呼ぶときは <code>removeFirst()</code
    > で先頭を取り出します。
  </p>
  <div class="grid">
    <div class="col">
      <ol class="events" aria-label="操作列">
        {#each WINDOW_EVENTS as e, i (i)}
          <li class:read={i < pos} class:now={i === pos - 1}>{label(e)}</li>
        {/each}
      </ol>
      <ol class="row-boxes" aria-label="配列 line の中身（左が先頭）">
        {#if line.length === 0}<li class="empty">空です</li>{/if}
        {#each line as v, i (v)}
          <li class="box" aria-label="{i + 1} 番目、{v}">{v}</li>
        {/each}
      </ol>
      <dl class="counters" aria-live="polite">
        <div>
          <dt>呼ばれた順</dt>
          <dd class="mono">{called.length === 0 ? '—' : called.join(', ')}</dd>
        </div>
        <div>
          <dt>前へ詰め直した人数（合計）</dt>
          <dd class="num">{shiftedSoFar}</dd>
        </div>
      </dl>
      <div class="row">
        <button type="button" class="btn btn-primary" onclick={() => (pos += 1)} disabled={done}
          >1 手進める</button
        >
        <button type="button" class="btn" onclick={() => (pos = sim.steps.length)} disabled={done}
          >最後まで</button
        >
      </div>
      {#if done}
        <p role="status">呼ばれた順は 1, 2, 3 で、答えは合っています。</p>
      {/if}
    </div>
    <div class="col">
      <CodeBlock code={CODE} label="配列と removeFirst() で処理するコード" />
    </div>
  </div>

  {#if done}
    <div class="scale">
      <p>
        <code>removeFirst()</code> は、呼ぶたびに残りの全員を 1 つ前へ詰め直します。来る人数を増やして、N
        人が来てから N 回呼ぶときの詰め直しを数えます。
      </p>
      <div class="row" role="group" aria-label="人数 N">
        {#each SIZES as s (s)}
          <button
            type="button"
            class="btn btn-sm"
            class:btn-primary={n === s}
            aria-pressed={n === s}
            onclick={() => (n = s)}>N = {formatCount(s)}</button
          >
        {/each}
      </div>
      {#if n !== null}
        <p class="big" role="status">
          詰め直し <strong class="num">{formatCount(shifts)}</strong> 回<span class="text-soft"
            >（(N − 1) + (N − 2) + … + 0）</span
          >。{#if seconds >= 3}1 秒に約 10^8 回とすると約 {Math.round(seconds)} 秒で、3 秒の制限に収まりません。{/if}
        </p>
      {/if}
      {#if n === 200_000}
        <p>
          呼んだ人を配列から消さずに、先頭の添字 <code>head</code> を 1 つ進めれば、要素の移動は 0
          回です。これがキュー（FIFO）の実装です。
          <a href="#/meter">計算量メーター</a>でも確かめられます。
        </p>
        <button type="button" class="btn btn-primary" onclick={ondone} data-testid="intro-done"
          >head 方式のキューで解く</button
        >
      {/if}
    </div>
  {/if}
</section>

<style>
  .intro {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .intro h2 {
    margin: 0;
  }
  .grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  .col {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .events {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .events li {
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    color: var(--text-soft);
    font-size: var(--fs-sm);
  }
  .events li.read {
    color: var(--text);
    border-color: var(--border-strong);
  }
  .events li.now {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .row-boxes {
    display: flex;
    gap: 6px;
    min-height: 3.2rem;
    margin: 0;
    padding: var(--space-2);
    list-style: none;
    background: var(--canvas);
    border-radius: var(--radius-md);
  }
  .box {
    display: grid;
    place-items: center;
    width: 2.8rem;
    height: 2.8rem;
    border-radius: var(--radius-md);
    border: 2px solid var(--border-strong);
    background: var(--surface);
    font-family: var(--font-mono);
    font-weight: var(--fw-bold);
  }
  .empty {
    align-self: center;
    color: var(--text-soft);
    font-size: var(--fs-sm);
  }
  .counters {
    display: flex;
    gap: var(--space-5);
    flex-wrap: wrap;
    margin: 0;
  }
  .counters div {
    display: flex;
    flex-direction: column;
  }
  .counters dt {
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
  .counters dd {
    margin: 0;
    font-size: var(--fs-lg);
    font-weight: var(--fw-bold);
  }
  .mono {
    font-family: var(--font-mono);
  }
  .num {
    font-variant-numeric: tabular-nums;
  }
  .scale {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: flex-start;
    padding: var(--space-4);
    border-radius: var(--radius-md);
    background: var(--accent-soft);
  }
  .scale p {
    margin: 0;
  }
  .big strong {
    font-size: var(--fs-xl);
  }
  @media (max-width: 1023px) {
    .grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
